import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HiBuildingOffice2 } from "react-icons/hi2";
import AuthService from "../../services/auth.service";

export default function NotBusinessPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    province: "",
    postal_code: "",
    country: "South Africa",
    email: "",
    phone: "",
    website: "",
    monthly_volume_target: "",
    billing_type: "",
  });

  const inputClass =
    "block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent";

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Business name is required.");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      const business = {
        name: form.name.trim(),
        address_line_1: form.address_line_1.trim() || undefined,
        address_line_2: form.address_line_2.trim() || undefined,
        city: form.city.trim() || undefined,
        province: form.province.trim() || undefined,
        postal_code: form.postal_code.trim() || undefined,
        country: form.country.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        website: form.website.trim() || undefined,
        account_tier: "business",
        monthly_volume_target: form.monthly_volume_target
          ? Number(form.monthly_volume_target)
          : undefined,
        billing_type: form.billing_type || undefined,
      };

      const createdBusiness = await AuthService.createBusiness(business);

      // Update the user in localStorage to include the new business
      AuthService.refreshUserWithBusiness(createdBusiness);

      navigate("/business/dashboard");
    } catch (err) {
      setError(
        err?.response?.data?.message ??
          "Failed to register business. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50 flex items-center justify-center px-4 py-10">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-lg w-full">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-violet-50 flex items-center justify-center mb-4">
            <HiBuildingOffice2 className="h-8 w-8 text-violet-500" />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-1">
            Register Your Business
          </h1>
          <p className="text-gray-500 text-sm">
            Set up a business account to access the Business Dashboard, create
            orders in bulk, and manage your shipments.
          </p>
        </div>

        {error && (
          <div
            className="p-4 text-sm text-red-800 rounded-lg bg-red-50 mb-4"
            role="alert"
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {/* Business name */}
          <div>
            <label
              htmlFor="biz_name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Business name <span className="text-red-500">*</span>
            </label>
            <input
              id="biz_name"
              type="text"
              required
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Tech Solutions SA"
              className={inputClass}
            />
          </div>

          {/* Address */}
          <div>
            <label
              htmlFor="address_line_1"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Address line 1{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="address_line_1"
              type="text"
              value={form.address_line_1}
              onChange={handleChange("address_line_1")}
              placeholder="123 Innovation Street"
              className={inputClass}
            />
          </div>

          <div>
            <label
              htmlFor="address_line_2"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Address line 2{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="address_line_2"
              type="text"
              value={form.address_line_2}
              onChange={handleChange("address_line_2")}
              placeholder="Suite 45"
              className={inputClass}
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label
                htmlFor="biz_city"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                City
              </label>
              <input
                id="biz_city"
                type="text"
                value={form.city}
                onChange={handleChange("city")}
                placeholder="Johannesburg"
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <label
                htmlFor="biz_province"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Province
              </label>
              <select
                id="biz_province"
                value={form.province}
                onChange={handleChange("province")}
                className={inputClass}
              >
                <option value="">Select</option>
                <option value="Gauteng">Gauteng</option>
                <option value="Western Cape">Western Cape</option>
                <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                <option value="Eastern Cape">Eastern Cape</option>
                <option value="Limpopo">Limpopo</option>
                <option value="Mpumalanga">Mpumalanga</option>
                <option value="North West">North West</option>
                <option value="Free State">Free State</option>
                <option value="Northern Cape">Northern Cape</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label
                htmlFor="postal_code"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Postal code
              </label>
              <input
                id="postal_code"
                type="text"
                value={form.postal_code}
                onChange={handleChange("postal_code")}
                placeholder="2000"
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <label
                htmlFor="biz_country"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Country
              </label>
              <input
                id="biz_country"
                type="text"
                value={form.country}
                onChange={handleChange("country")}
                placeholder="South Africa"
                className={inputClass}
              />
            </div>
          </div>

          {/* Contact */}
          <div>
            <label
              htmlFor="biz_email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Business email{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="biz_email"
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              placeholder="info@techsolutions.co.za"
              className={inputClass}
            />
          </div>

          <div>
            <label
              htmlFor="biz_phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Business phone{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="biz_phone"
              type="tel"
              value={form.phone}
              onChange={handleChange("phone")}
              placeholder="+27 11 123 4567"
              className={inputClass}
            />
          </div>

          <div>
            <label
              htmlFor="biz_website"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Website{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="biz_website"
              type="url"
              value={form.website}
              onChange={handleChange("website")}
              placeholder="https://www.example.co.za"
              className={inputClass}
            />
          </div>

          {/* Shipping preferences */}
          <div>
            <label
              htmlFor="monthly_volume"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Est. monthly parcel volume{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="monthly_volume"
              type="number"
              min="0"
              value={form.monthly_volume_target}
              onChange={handleChange("monthly_volume_target")}
              placeholder="500"
              className={inputClass}
            />
          </div>

          <div>
            <label
              htmlFor="billing_type"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Billing preference{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              id="billing_type"
              value={form.billing_type}
              onChange={handleChange("billing_type")}
              className={inputClass}
            >
              <option value="">Select billing type</option>
              <option value="order_to_order">Order to order</option>
              <option value="monthly_invoicing">Monthly invoicing</option>
              <option value="prepaid">Prepaid Wallet</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full mt-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-all shadow-lg hover:shadow-xl"
          >
            {isLoading ? "Creating business..." : "Register Business"}
          </button>
        </form>

        <div className="mt-4 text-center">
          <Link
            to="/"
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
